import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
    Table,
    Tag,
    Space,
    Button,
    Modal,
    Tooltip,
    message,
    Pagination,
    Card,
    Statistic,
    Row,
    Col,
    Typography
} from 'antd';
import {
    FaEye,
    FaTrash,
    FaExternalLinkAlt,
    FaUser,
    FaEnvelope,
    FaMapMarkerAlt,
    FaShoppingCart,
    FaPowerOff
} from 'react-icons/fa';
import { api } from '../../config/api';
import Loader from '../shared/loader/loader';

const { Title, Text } = Typography;

const WebsiteCalculatorEstimates = ({ searchQuery = "" }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedEstimate, setSelectedEstimate] = useState(null);
    const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [estimateToDelete, setEstimateToDelete] = useState(null);
    const [activeTab, setActiveTab] = useState("estimates");

    const toggleOptions = [
        { key: "estimates", label: "Estimates" },
        { key: "links", label: "Links" }
    ];

    function formatDate(dateString) {
        const options = { year: "numeric", month: "short", day: "numeric" };
        const formattedDate = new Date(dateString).toLocaleDateString(
            undefined,
            options
        );
        return formattedDate;
    }

    // Fetch guest estimates data
    const {
        data: guestEstimatesData,
        isLoading: estimatesLoading,
        refetch: refetchEstimates
    } = useQuery(
        ['webAdminGuestEstimates'],
        async () => {
            const response = await api.get('/ior-ous-calculator/admin/web-estimates');
            return response.data;
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes cache
        }
    );

    // Fetch external links data
    const {
        data: externalLinksData,
        isLoading: linksLoading,
        refetch: refetchLinks
    } = useQuery(
        ['webAdminExternalLinks'],
        async () => {
            const response = await api.get('/ior-ous-calculator/admin/web-estimates/links');
            return response.data;
        },
        {
            refetchOnWindowFocus: false,
            staleTime: 5 * 60 * 1000, // 5 minutes cache
        }
    );

    // Delete mutation for estimates
    const deleteMutation = useMutation(
        async (estimateId) => {
            await api.delete(`/ior-ous-calculator/admin/web-estimates/${estimateId}`);
        },
        {
            onSuccess: () => {
                message.success('Guest estimate deleted successfully');
                setIsDeleteModalVisible(false);
                setEstimateToDelete(null);
                refetchEstimates();
            },
            onError: (error) => {
                message.error(`Failed to delete estimate: ${error.response?.data?.message || error.message}`);
            }
        }
    );

    // Toggle link status mutation
    const toggleLinkStatusMutation = useMutation(
        async ({ linkId, newStatus }) => {
            await api.put(`/ior-ous-calculator/admin/web-estimates/links/${linkId}/toggle-status`, { status: newStatus });
        },
        {
            onSuccess: () => {
                message.success('Link status updated successfully');
                refetchLinks();
            },
            onError: (error) => {
                message.error(`Failed to update link status: ${error.response?.data?.message || error.message}`);
            }
        }
    );

    // Filter data based on search query
    const filteredEstimates = useMemo(() => {
        if (!guestEstimatesData?.data) return [];
        if (searchQuery.trim() === "") return guestEstimatesData.data;
        return guestEstimatesData.data.filter((estimate) =>
            Object.values(estimate).some(
                (value) =>
                    typeof value === "string" &&
                    value.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, guestEstimatesData?.data]);

    const filteredLinks = useMemo(() => {
        if (!externalLinksData?.data) return [];
        if (searchQuery.trim() === "") return externalLinksData.data;
        return externalLinksData.data.filter((link) =>
            Object.values(link).some(
                (value) =>
                    typeof value === "string" &&
                    value.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    }, [searchQuery, externalLinksData?.data]);

    // Pagination logic
    const getPaginatedData = (data) => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return data.slice(startIndex, endIndex);
    };

    const handleViewDetails = async (estimateId) => {
        try {
            const response = await api.get(`/ior-ous-calculator/admin/web-estimates/${estimateId}`);
            setSelectedEstimate(response.data.data);
            setIsDetailModalVisible(true);
        } catch (error) {
            message.error('Failed to fetch estimate details');
        }
    };

    const handleDelete = (estimate) => {
        setEstimateToDelete(estimate);
        setIsDeleteModalVisible(true);
    };

    const confirmDelete = () => {
        if (estimateToDelete) {
            deleteMutation.mutate(estimateToDelete._id);
        }
    };

    const handleToggleLinkStatus = (link) => {
        const newStatus = link.effectiveStatus === 'disabled' ? 'active' : 'disabled';
        if (new Date() > new Date(link.expiryTime) && newStatus === 'active') {
            message.error('Cannot enable an expired link');
            return;
        }
        toggleLinkStatusMutation.mutate({ linkId: link.linkId, newStatus });
    };

    const handlePageChange = (page, size) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'green';
            case 'expired': return 'red';
            case 'disabled': return 'orange';
            default: return 'default';
        }
    };

    const getResponseStatus = (record) => {
        if (record.customerResponseStatus) return record.customerResponseStatus;
        if (record.estimationAccept) return 'accepted';
        return 'pending';
    };

    const renderResponseStatus = (record) => {
        const status = getResponseStatus(record);

        if (status === 'accepted') {
            return <Tag color="green">Accepted</Tag>;
        }

        if (status === 'rejected') {
            return <Tag color="red">Rejected</Tag>;
        }

        return <Tag color="default">Pending</Tag>;
    };

    // Helper: Get valid services (value > 0)
    const getValidServices = (services = []) => {
        return services.filter(s => {
            const amount = s.total ?? s.value ?? s.exclVAT ?? 0;
            return parseFloat(amount) > 0;
        });
    };

    // Helper: Calculate correct total
    const calculateTotal = (estimate) => {
        let total = 0;
        const services = estimate.estimateSummary?.services || [];
        getValidServices(services).forEach(s => {
            total += parseFloat(s.total ?? s.value ?? s.exclVAT ?? 0) || 0;
        });
        if (estimate.estimateSummary?.dhlTaxes) {
            estimate.estimateSummary.dhlTaxes.forEach(tax => {
                if (tax.value) total += parseFloat(tax.value) || 0;
                if (tax.details) {
                    tax.details.forEach(d => total += parseFloat(d.value) || 0);
                }
            });
        }
        if (total === 0 && estimate.estimateSummary?.total) {
            total = parseFloat(estimate.estimateSummary.total) || 0;
        }
        return total;
    };

    const estimateColumns = [
        {
            title: 'Estimate ID',
            dataIndex: 'estimateId',
            key: 'estimateId',
            render: (text) => <Text strong>#{text}</Text>,
        },
        {
            title: 'User Info',
            key: 'userInfo',
            render: (_, record) => (
                <div>
                    <div className="flex items-center space-x-1">
                        <FaUser className="text-gray-500" />
                        <Text>{record.userName}</Text>
                    </div>
                    <div className="flex items-center space-x-1">
                        <FaEnvelope className="text-gray-500" />
                        <Text type="secondary">{record.email}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: 'Link Info',
            key: 'linkInfo',
            render: (_, record) => (
                <div>
                    <div className="flex items-center space-x-1">
                        {/* <FaExternalLinkAlt className="text-blue-500" /> */}
                        <Text>{record.company_name || 'N/A'}</Text>
                    </div>
                    <Tag color={getStatusColor(record.linkStatus)}>
                        {record.linkStatus || 'Unknown'}
                    </Tag>
                </div>
            ),
        },
        {
            title: 'Route',
            key: 'route',
            render: (_, record) => (
                <div className="flex items-center space-x-1">
                    <FaMapMarkerAlt className="text-red-500" />
                    <div>
                        <div>{record.originCity}, {record.originCountry}</div>
                        <div className="text-gray-500">↓</div>
                        <div>{record.destinationCity}, {record.destinationCountry}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Service & Total',
            key: 'serviceTotal',
            render: (_, record) => {
                const currency = record.estimateSummary?.currency || record.itemsCurrency || 'USD';
                const total = calculateTotal(record);

                return (
                    <div>
                        <div className="flex items-center space-x-1">
                            <FaShoppingCart className="text-green-500" />
                            <Text>{record.selectedService}</Text>
                        </div>
                        {total > 0 && (
                            <Text strong>
                                {currency} {total.toFixed(2)}
                            </Text>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Created Date',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => formatDate(date),
        },
        {
            title: 'Status',
            key: 'customerResponseStatus',
            render: (_, record) => renderResponseStatus(record),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Tooltip title="View Details">
                        <Button
                            type="primary"
                            size="small"
                            icon={<FaEye />}
                            onClick={() => handleViewDetails(record._id)}
                        />
                    </Tooltip>
                    <Tooltip title="Delete">
                        <Button
                            danger
                            size="small"
                            icon={<FaTrash />}
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const linkColumns = [
        {
            title: 'Link Name',
            dataIndex: 'company_name',
            key: 'company_name',
        },
        {
            title: 'Link ID',
            dataIndex: 'linkId',
            key: 'linkId',
        },
        {
            title: 'Status',
            dataIndex: 'effectiveStatus',
            key: 'effectiveStatus',
            render: (status) => (
                <Tag color={getStatusColor(status)}>{status}</Tag>
            ),
        },
        {
            title: 'Expiry Time',
            dataIndex: 'expiryTime',
            key: 'expiryTime',
            render: (date) => formatDate(date),
        },
        {
            title: 'Max Estimates',
            dataIndex: 'maxEstimates',
            key: 'maxEstimates',
        },
        {
            title: 'Estimates Created',
            dataIndex: 'estimatesCreated',
            key: 'estimatesCreated',
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => formatDate(date),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => {
                if (record.effectiveStatus === 'expired') {
                    return null; // Hide button for expired links
                }
                return (
                    <Space>
                        <Tooltip title={record.effectiveStatus === 'disabled' ? 'Enable' : 'Disable'}>
                            <Button
                                type={record.effectiveStatus === 'disabled' ? 'default' : 'primary'}
                                size="small"
                                icon={<FaPowerOff />}
                                onClick={() => handleToggleLinkStatus(record)}
                            />
                        </Tooltip>
                    </Space>
                );
            },
        },
    ];

    const currentFullData = activeTab === "estimates" ? filteredEstimates : filteredLinks;
    const paginatedData = getPaginatedData(currentFullData);
    const totalItems = currentFullData.length;

    if ((activeTab === "estimates" && estimatesLoading) || (activeTab === "links" && linksLoading)) {
        return (
            <div className="mt-4 p-3 bg-white rounded-md md:p-5">
                <Loader />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Toggle Buttons */}
            <div className="py-2 px-2 shadow-md flex justify-center rounded-xl border border-gray-300 bg-gray-100">
                <div className="flex bg-white rounded-lg p-1 gap-1 relative">
                    {toggleOptions.map((option) => (
                        <button
                            key={option.key}
                            onClick={() => setActiveTab(option.key)}
                            className={`${activeTab === option.key
                                ? "btnBg text-white shadow-lg shadow-[#6478b0]"
                                : "bg-transparent text-gray-600 hover:text-gray-800 btnHover"
                                } text-sm font-medium rounded-md text-center py-2 px-6 transition-all duration-200 ease-in-out relative z-10`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Statistics Cards */}
            {guestEstimatesData?.stats && (
                <Row gutter={16} className="mb-4">
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Guest Estimates"
                                value={guestEstimatesData.stats.totalGuestEstimates}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Active Links"
                                value={guestEstimatesData.stats.activeLinks}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Expired Links"
                                value={guestEstimatesData.stats.expiredLinks}
                                valueStyle={{ color: '#cf1322' }}
                            />
                        </Card>
                    </Col>
                    <Col span={6}>
                        <Card>
                            <Statistic
                                title="Total Links"
                                value={guestEstimatesData.stats.totalLinks}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Table */}
            <div className="bg-white rounded-md">
                <Table
                    columns={activeTab === "estimates" ? estimateColumns : linkColumns}
                    dataSource={paginatedData}
                    pagination={false}
                    rowKey="_id"
                    size="middle"
                />

                {/* Pagination */}
                <div className="mt-4 flex justify-center p-4">
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalItems}
                        onChange={handlePageChange}
                        showSizeChanger
                        showQuickJumper
                        showTotal={(total, range) =>
                            `${range[0]}-${range[1]} of ${total} items`
                        }
                    />
                </div>
            </div>

            {/* Detail Modal */}
            <Modal
                title={`Guest Estimate Details - #${selectedEstimate?.estimateId}`}
                visible={isDetailModalVisible}
                onCancel={() => setIsDetailModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedEstimate && (
                    <div className="space-y-4">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Card title="User Information" size="small">
                                    <p><strong>Name:</strong> {selectedEstimate.userName}</p>
                                    <p><strong>Email:</strong> {selectedEstimate.email}</p>
                                </Card>
                            </Col>
                            <Col span={12}>
                                <Card title="Link Information" size="small">
                                    <p><strong>Link Name:</strong> {selectedEstimate.linkInfo?.company_name || 'N/A'}</p>
                                    <p><strong>Link Status:</strong>
                                        <Tag color={getStatusColor(selectedEstimate.linkInfo?.effectiveStatus)} className="ml-2">
                                            {selectedEstimate.linkInfo?.effectiveStatus || 'Unknown'}
                                        </Tag>
                                    </p>
                                    <p><strong>Response Status:</strong> <span className="ml-2">{renderResponseStatus(selectedEstimate)}</span></p>
                                </Card>
                            </Col>
                        </Row>

                        <Card title="Shipping Details" size="small">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <p><strong>Origin:</strong> {selectedEstimate.originCity}, {selectedEstimate.originCountry}</p>
                                    <p><strong>Service:</strong> {selectedEstimate.selectedService}</p>
                                </Col>
                                <Col span={12}>
                                    <p><strong>Destination:</strong> {selectedEstimate.destinationCity}, {selectedEstimate.destinationCountry}</p>
                                    <p><strong>Currency:</strong> {selectedEstimate.itemsCurrency}</p>
                                </Col>
                            </Row>
                        </Card>

                        {selectedEstimate.estimateSummary && (
                            <Card title="Estimate Summary" size="small">
                                <p>
                                    <strong>Total:</strong>{' '}
                                    {(() => {
                                        const currency = selectedEstimate.estimateSummary.currency || selectedEstimate.itemsCurrency || 'USD';
                                        const total = calculateTotal(selectedEstimate);
                                        return `${currency} ${Number(total).toFixed(2)}`;
                                    })()}
                                </p>

                                {(() => {
                                    const validServices = getValidServices(selectedEstimate.estimateSummary.services);
                                    if (validServices.length === 0) return null;

                                    return (
                                        <div>
                                            <strong>Services:</strong>
                                            <ul className="ml-4">
                                                {validServices.map((service, index) => {
                                                    const amount = service.total ?? service.value ?? service.exclVAT ?? 0;
                                                    return (
                                                        <li key={index}>
                                                            {service.description || service.name}: {Number(amount).toFixed(2)}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    );
                                })()}
                            </Card>
                        )}

                        {selectedEstimate.items && selectedEstimate.items.length > 0 && (
                            <Card title="Items" size="small">
                                <div className="max-h-40 overflow-y-auto">
                                    {selectedEstimate.items.map((item, index) => (
                                        <div key={index} className="border-b pb-2 mb-2">
                                            <p><strong>{item.itemName}</strong></p>
                                            <p>Value: {item.itemValue} | Quantity: {item.quantity}</p>
                                            <p>Origin: {item.countryOrigin}</p>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}
                    </div>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title="Confirm Delete"
                visible={isDeleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => setIsDeleteModalVisible(false)}
                okText="Yes, Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true, loading: deleteMutation.isLoading }}
            >
                <p>Are you sure you want to delete this guest estimate?</p>
                {estimateToDelete && (
                    <div className="mt-2 p-3 bg-gray-50 rounded">
                        <p><strong>Estimate ID:</strong> #{estimateToDelete.estimateId}</p>
                        <p><strong>User:</strong> {estimateToDelete.userName}</p>
                        <p><strong>Email:</strong> {estimateToDelete.email}</p>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default WebsiteCalculatorEstimates;
